/** CAMERELE SE DERIVĂ DIN PEREȚI. Ce rămâne din anvelopă după ce scoți zidurile.
 *
 *  DE CE. Până aici o cameră era un poligon de sine stătător, scris în document
 *  lângă pereți și transcris separat de ei din aceeași planșă — atât de separat
 *  încât vârfurile lor nici nu coincid (3 mm între fața unui zid și cea a
 *  camerei de lângă). Când muți un perete, „ce parte din cameră ține de el" nu
 *  se poate decât GHICI: se căuta o muchie de cameră care cade pe fața zidului.
 *  Măsurat pe M5: ghicitul nimerea la 92 de pereți din 113. Iar un plan în care
 *  patru din cinci laturi ale unei camere se mișcă e mai stricat decât unul în
 *  care nu se mișcă niciuna — de-aia „camerele arată ciudat".
 *
 *  Aici nu se mai ghicește. O cameră E golul dintre ziduri, prin construcție:
 *  muți un perete, camerele din jur se refac. Exact motivul pentru care în
 *  Homestyler nu se poate rupe planul.
 *
 *  CE SE PĂSTREAZĂ DE LA CAMERA VECHE, fiindcă geometria nu le poate spune:
 *
 *    * FELUL (`kind`), latura și apartamentul — «baie» nu se vede din formă;
 *    * `k`-ul vârfurilor, adică elasticitatea modulului. Un vârf poartă
 *      `a + k·delta`; un poligon refăcut ar ieși cu `k = 0` peste tot, iar
 *      modulul ar deveni rigid în tăcere. Fiecare vârf nou îl ia pe cel al celui
 *      mai apropiat vârf vechi al camerei cu care s-a potrivit.
 *
 *  POTRIVIREA E PE SUPRAPUNERE, nu pe ordine: o cameră mutată rămâne aceeași
 *  cameră cât timp acoperă în cea mai mare parte locul celei vechi. Piesele
 *  rămase nepotrivite sunt camere NOI — cineva a împărțit una în două cu un
 *  zid.
 *
 *  SE DESPARTE SINGURĂ, NU SE UNEȘTE SINGURĂ NICIODATĂ. Un zid nou chiar spune
 *  ceva: aici sunt de-acum două încăperi. Un zid SCOS nu spune că cele două
 *  camere au devenit una — spune doar că nu mai e perete între ele, iar
 *  bucătăria deschisă spre living rămâne bucătărie. Deci o bucată în care
 *  locuiesc două camere se împarte înapoi între ele (v. `impartita`), iar
 *  unirea se cere din buton, care scoate camera absorbită din document (v.
 *  `model::uneste`). Cerut explicit, 16.09.2026.
 *
 *  ────────────────────────────────────────────────────────────────────────────
 *  NU E LEGAT ÎNCĂ LA EDITOR, și motivul e o măsurătoare, nu o scăpare.
 *
 *  Pe catalogul de azi, derivarea dă MAI PUȚINE camere decât are documentul:
 *
 *      M2:  9 camere scrise ->  7 derivate
 *      M5: 21 camere scrise -> 15 derivate
 *
 *  Nu e o greșeală de calcul. Sunt camere care chiar comunică între ele: pe M2,
 *  livingul (15,09) și holul lui (5,11) ies ca o singură bucată de 19,9 fiindcă
 *  între ele nu e niciun zid, ci o trecere liberă. Un spațiu definit de ziduri
 *  NU le poate despărți — nici Homestyler n-ar putea; acolo tot ce e deschis
 *  unul spre altul e o cameră.
 *
 *  DOUĂ LUCRURI AU TREBUIT AFLATE PE DRUM, amândouă lăsate în cod mai jos:
 *
 *    1. anvelopa declarată e mai mare decât clădirea (fațada lui M2 stă la
 *       y = ±7,504 într-o anvelopă de ±7,65), iar fâșia de 14 cm din jur lega
 *       toate camerele pe la spate — de-aia se taie la cadrul zidurilor;
 *    2. ZIDURILE SUNT TĂIATE LA FIECARE GOL. Așa au fost măsurate golurile
 *       (`origin: "cut"` — planșa taie stratul ZIDURI chiar în dreptul ușii),
 *       deci fiecare ușă e o spărtură adevărată în poligon, iar spațiul liber
 *       curge prin ea. Fără astuparea golurilor, tot etajul ieșea o cameră.
 *
 *  Ce rămâne de decis nu e tehnic: dacă documentul trece pe camere derivate,
 *  ariile declarate ale catalogului se schimbă (și cu ele `useful_m2` și
 *  `alpha`, adică obiectivul pe care optimizează planul de situație). Alea sunt
 *  cifrele pe care se vinde un apartament, deci schimbarea lor e o hotărâre, nu
 *  o consecință a unui refactor.
 */
// Tipurile de aici, operațiile din `taietor`: build-ul ESM al bibliotecii n-are
// export-uri numite, deci `pc.intersection` e `undefined` în browser, oricât de
// mult ar promite `.d.ts`-ul ei. V. `viewer/taietor`.
import type * as pc from "polygon-clipping";
import { taie } from "../../taietor";
import { anvelopa, inInel, type CameraDoc, type Pt, type TronsonDoc, type VarfAfin }
  from "../doc";
import { FEL_IMPLICIT } from "../feluri";
import { inelCuGaura } from "./model";


/** Sub atâta, o bucată e o așchie dintre două ziduri care aproape se ating, nu
 *  o cameră. Cea mai mică încăpere din catalog e o baie de 3,89 m². */
export const ASCHIE_M2 = 0.6;

export type Inel = [number, number][];

/** Cât de lat se taie un prag.
 *
 *  Zero n-ar despărți nimic — un segment n-are arie, iar din poligon nu se
 *  scade nimic. Orice grosime însă mușcă din camerele de-o parte și de alta,
 *  deci se ia cât mai puțin cât taie sigur: un centimetru fură 5 mm de fiecare
 *  parte, adică sub rotunjirea la centimetru pătrat a ariei. */
const PRAG_LAT = 0.01;

const inelul = (poly: VarfAfin[]): Inel =>
  poly.map((q) => [q[0].a, q[1].a] as [number, number]);

/** CÂT SE LUNGEȘTE UN ZID LA CAPETE înainte de scădere.
 *
 *  Fără asta, derivarea întoarce O SINGURĂ cameră pe tot modulul. Zidurile
 *  transcrise nu se ating: între capătul unuia și fața celuilalt sunt câțiva
 *  milimetri, iar golul dintre ele leagă camerele una de alta pe sub perete.
 *  Măsurat: pe M2, scăderea celor 34 de ziduri din anvelopă dădea o bucată de
 *  82,2 m² în loc de nouă camere.
 *
 *  6 cm: mai mult decât slăbiciunea transcrierii (3 mm măsurat) și mai puțin
 *  decât orice trecere pe care cineva ar desena-o între două camere. Se lungește
 *  DOAR pe axa lungă a zidului — un zid îngroșat ar mânca din camere, unul
 *  lungit nu face decât să-și închidă îmbinările.
 */
const PRELUNGIRE = 0.06;

/** CÂT DE LARG E UN GOL PE CARE PRELUNGIREA NU-L MAI POATE ÎNCHIDE: două
 *  prelungiri, una din fiecare capăt. Sub atât, cele două capete chiar se
 *  ating, deci lungirea își face treaba; peste, ea doar mănâncă din gol. */
const ROST_MAX = 2 * PRELUNGIRE;

/** …ȘI CÂT DE LARG POATE FI ÎNCĂ UN GOL DE PERETE: 2,50 m, aceeași cifră ca
 *  pragul arcadelor din `cote::golurileDintreZiduri`. Peste atât nu mai e un
 *  gol, sunt doi pereți — iar pe o planșă IMPORTATĂ zidurile colineare se
 *  întrerup la tot pasul, de la o transcriere la alta. Fără capul ăsta,
 *  prelungirea se oprea la fiecare asemenea întrerupere și camerele curgeau una
 *  în alta pe sub perete — exact ce păzește `PRELUNGIRE`. */
const GOL_MAX = 2.5;

/** Cadrul unui inel, pe axe. */
function cadrul(ring: Inel) {
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  return { x0: Math.min(...xs), x1: Math.max(...xs),
           y0: Math.min(...ys), y1: Math.max(...ys) };
}

/** DĂ CAPĂTUL ĂSTA ÎNTR-O ARCADĂ? Atunci nu se lungește.
 *
 *  `PRELUNGIRE` închide ÎMBINĂRI — rosturi de milimetri între capătul unui zid
 *  și fața altuia. Un GOL dintre două ziduri colineare nu e un rost: e locul pe
 *  unde se trece. Lungit în el, fiecare capăt îi mănâncă 6 cm, deci camerele se
 *  derivă pe un gol cu 12 cm mai îngust decât cel desenat — și de-acolo veneau
 *  AMÂNDOUĂ neajunsurile arătate de arhitect (21.10.2026): o spărtură de 0,80 m
 *  cotată «0,68», și câte un dreptunghi alb de 6 cm între camera oprită prea
 *  devreme și capătul adevărat al zidului. Aceeași scădere de 0,12 m se vedea și
 *  pe 0,85 → 0,73.
 *
 *  SE ÎNTREABĂ NUMAI DE GOLURI PE CARE PRELUNGIREA N-AR PUTEA SĂ LE ÎNCHIDĂ
 *  ORICUM (v. `ROST_MAX`), și numai de ziduri COLINEARE, DE ACEEAȘI GROSIME:
 *  atât înseamnă «același perete, cu un gol în el». Orice altă îmbinare — un T,
 *  un colț, un capăt liber — se lungește mai departe, deci tot ce mergea până
 *  acum merge la fel. */
function daInGol(eu: Inel, toate: readonly Inel[], peX: boolean,
                 spreSus: boolean): boolean {
  const a = cadrul(eu);
  const [s0, s1] = peX ? [a.x0, a.x1] : [a.y0, a.y1];
  const [f0, f1] = peX ? [a.y0, a.y1] : [a.x0, a.x1];
  const capat = spreSus ? s1 : s0;
  return toate.some((alt) => {
    if (alt === eu) return false;
    const b = cadrul(alt);
    const [t0, t1] = peX ? [b.x0, b.x1] : [b.y0, b.y1];
    const [g0, g1] = peX ? [b.y0, b.y1] : [b.x0, b.x1];
    // aceeași linie și aceeași grosime: fețele cad una peste alta
    if (Math.abs(f0 - g0) > 0.005 || Math.abs(f1 - g1) > 0.005) return false;
    const rost = spreSus ? t0 - capat : capat - t1;
    return rost > ROST_MAX && rost <= GOL_MAX;
  });
}

/** ROSTUL până la zidul COLINEAR următor, de partea cerută — `Infinity` dacă
 *  nu e niciunul. Aceeași socoteală ca `daInGol`, dar întoarce cifra, nu
 *  răspunsul: din ea se vede dacă lungirea mai are ce închide. */
function rostulLa(eu: Inel, toate: readonly Inel[], peX: boolean,
                  spreSus: boolean): number {
  const a = cadrul(eu);
  const [s0, s1] = peX ? [a.x0, a.x1] : [a.y0, a.y1];
  const [f0, f1] = peX ? [a.y0, a.y1] : [a.x0, a.x1];
  const capat = spreSus ? s1 : s0;
  let cel = Infinity;
  for (const alt of toate) {
    if (alt === eu) continue;
    const b = cadrul(alt);
    const [t0, t1] = peX ? [b.x0, b.x1] : [b.y0, b.y1];
    const [g0, g1] = peX ? [b.y0, b.y1] : [b.x0, b.x1];
    if (Math.abs(f0 - g0) > 0.005 || Math.abs(f1 - g1) > 0.005) continue;
    const rost = spreSus ? t0 - capat : capat - t1;
    if (rost >= -1e-9 && rost < cel) cel = rost;
  }
  return cel;
}

/** Zidul, lungit la capetele care chiar au o îmbinare de închis.
 *  EXPORTAT PENTRU PROBE: regula stă aici, iar prin camerele derivate ea se
 *  vede numai ca o abatere de 6 cm într-o cifră, adică prea târziu. */
export function lungit(ring: Inel, toate: readonly Inel[] = [],
                       /** CAPETELE ACESTUI ZID SUNT LĂSATE LIBERE DE OM.
                        *
                        *  Portița de mână cerută de arhitect (27.10.2026):
                        *  «give me the manual escape so I can fill that gap
                        *  manually». De la OB-18 un capăt care iese în gol nu
                        *  se mai lungește oricum; steagul mai contează lângă un
                        *  zid de-a curmezișul, la mai puțin de `ROST_MAX`: acolo
                        *  lungirea închide rostul, iar steagul îl lasă deschis
                        *  și podeaua trece prin el.
                        *
                        *  O ÎMBINARE STRÂNSĂ COLINIARĂ NU SE DESCHIDE: se scoate
                        *  lungirea NUMAI de la capetele al căror rost până la
                        *  zidul din prelungire e mai mare decât `ROST_MAX`.
                        *  Lângă un zid de-a curmezișul însă steagul lasă două
                        *  camere să curgă una în alta prin rost. */
                       liber = false,
                       /** The wall's written axis: the direction of an oblique
                        *  wall, whose box is near-square. */
                       axis?: readonly (readonly number[])[] | null,
                       /** A curved wall's bulge: not 0 means its ends are
                        *  drawn by the editor and already meet. */
                       bulge = 0): Inel {
  const { x0, x1, y0, y1 } = cadrul(ring);
  const w = x1 - x0;
  const h = y1 - y0;
  // Axa lungă. Un zid curb are cadrul aproape pătrat și nu se lungește: acolo
  // capetele sunt oricum desenate de editor, deci se ating.
  if (Math.abs(w - h) < 0.05) return lungitPeAxa(ring, toate, liber, axis, bulge);
  const peX = w > h;
  const mij = peX ? (x0 + w / 2) : (y0 + h / 2);
  // Fiecare capăt se întreabă separat: un zid spart are un capăt în arcadă și
  // unul în îmbinarea de la celălalt cap, iar ele nu se hotărăsc împreună.
  /** Se lungește capătul ăsta? */
  const lungesc = (spreSus: boolean) => {
    if (daInGol(ring, toate, peX, spreSus)) return false;
    // Steagul omului taie numai lungirile fără rost — v. nota de sus.
    if (liber && rostulLa(ring, toate, peX, spreSus) > ROST_MAX) return false;
    const capat = spreSus ? (peX ? x1 : y1) : (peX ? x0 : y0);
    const semn = spreSus ? 1 : -1;
    return areVecin(ring, toate, ring.filter((p) => Math.abs((peX ? p[0] : p[1]) - capat) < 0.01),
                    peX ? [semn, 0] : [0, semn]);
  };
  const jos = lungesc(false);
  const sus = lungesc(true);
  return ring.map((p) => {
    const t = peX ? p[0] : p[1];
    const d = t < mij ? (jos ? -PRELUNGIRE : 0) : (sus ? PRELUNGIRE : 0);
    return (peX ? [p[0] + d, p[1]] : [p[0], p[1] + d]) as [number, number];
  });
}

/** A CORE PART TAKES ITS SHAFT WITH IT.
 *
 *  A lift's part is its cabin's footprint; between it and the shaft walls
 *  there are a few centimetres of clearance (2–4 cm on M3), and the landing
 *  door leaves a reveal in the shaft wall. Subtracted as drawn, the part left
 *  that clearance joined to the hall through the door gap. So behind each
 *  side, every wall line parallel to it whose near face is within `ROST_MAX`
 *  adds a strip from the side to the line's FAR face, over the span that
 *  line covers (its pieces and the gaps between them, so the door's reveal
 *  goes too). Walls are subtracted anyway, so the strips only take the
 *  clearance and the reveal, and stop where the room past the wall begins.
 *
 *  Strips, not a grown rectangle: a side grown along its whole length took the
 *  floor behind a shaft wall that had been dragged away (M5, W49). */
function crescutaLaZiduri(
  [u0, v0, u1, v1]: Cutie,
  pereti: NonNullable<NonNullable<TronsonDoc["plan"]>["walls"]>,
): Cutie[] {
  const cutii = pereti.map((w) => cutia(inelul(w.poly)));
  const out: Cutie[] = [[u0, v0, u1, v1]];
  /** The strips behind one side: `fata` along `peX`, outward `semn`, over
   *  the span `s0`–`s1` across. */
  const fasii = (peX: boolean, fata: number, semn: 1 | -1, s0: number, s1: number) => {
    const linii = new Map<string, { departe: number; t0: number; t1: number }>();
    for (const [a0, b0, a1, b1] of cutii) {
      const [c0, c1, t0, t1] = peX ? [a0, a1, b0, b1] : [b0, b1, a0, a1];
      if (c1 - c0 >= t1 - t0) continue;       // not running along this side
      if (t1 <= s0 + 1e-6 || t0 >= s1 - 1e-6) continue;
      const aproape = semn > 0 ? c0 - fata : fata - c1;
      if (aproape < -0.01 || aproape > ROST_MAX) continue;
      const departe = semn > 0 ? c1 : c0;
      const k = departe.toFixed(2);
      const l = linii.get(k);
      linii.set(k, l ? { departe, t0: Math.min(l.t0, t0), t1: Math.max(l.t1, t1) }
                     : { departe, t0, t1 });
    }
    for (const { departe, t0, t1 } of linii.values()) {
      const [a, b] = [Math.max(s0, t0), Math.min(s1, t1)];
      const [c0, c1] = semn > 0 ? [fata, departe] : [departe, fata];
      out.push(peX ? [c0, a, c1, b] : [a, c0, b, c1]);
    }
  };
  fasii(true, u0, -1, v0, v1);
  fasii(true, u1, 1, v0, v1);
  fasii(false, v0, -1, u0, u1);
  fasii(false, v1, 1, u0, u1);
  return out;
}

/** IS THERE SOMETHING TO CLOSE AT THIS END? Another wall AHEAD of the end
 *  cap: on a line from the cap along `spre`, within `ROST_MAX`, from any of
 *  five points across the cap. The midpoint alone missed a wall covering
 *  half the cap: a dragged wall's remaining piece under a partition, whose
 *  midpoint fell 0.5 mm past the piece's end, so the joint stayed open and
 *  two rooms ran together (the imported plan, 6 drags).
 *
 *  Lengthening exists to close the few millimetres a transcription leaves at
 *  a joint. At a free end it closed nothing and bit 6 cm out of the room: the
 *  white rectangle and the «2,06» cote of row 92 (OB-18).
 *
 *  Ahead, not merely near: an end flush with the far face of a crossing wall
 *  line has walls on both sides of its cap and nothing in front, and
 *  lengthened it bit 6 cm out of the room past that face (M5 W6, M8 W19;
 *  0.02 m² each). The walls beside it are closed against it by their own
 *  ends, which do have it ahead. */
function areVecin(eu: Inel, toate: readonly Inel[], cap: readonly Pt[],
                  spre: Pt): boolean {
  if (!cap.length) return true;
  // The cap's two ends: its extreme points across `spre`.
  const peste = (p: Pt) => p[0] * spre[1] - p[1] * spre[0];
  const [a, b] = cap.reduce<[Pt, Pt]>(([lo, hi], p) =>
    [peste(p) < peste(lo) ? p : lo, peste(p) > peste(hi) ? p : hi], [cap[0], cap[0]]);
  const puncte: Pt[] = [];
  for (const f of [0.05, 0.25, 0.5, 0.75, 0.95]) {
    const o: Pt = [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
    for (let s = 0.002; s <= ROST_MAX + 1e-9; s += 0.004) {
      puncte.push([o[0] + spre[0] * s, o[1] + spre[1] * s]);
    }
  }
  const acelasi = (r: Inel) => r === eu || (r.length === eu.length
    && r.every((p, i) => p[0] === eu[i][0] && p[1] === eu[i][1]));
  return toate.some((alt) => !acelasi(alt) && puncte.some((p) => inauntru(p, alt)));
}

/** Even-odd point in ring. */
function inauntru(p: Pt, r: Inel): boolean {
  let da = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    if ((r[i][1] > p[1]) !== (r[j][1] > p[1])
        && p[0] < ((r[j][0] - r[i][0]) * (p[1] - r[i][1])) / (r[j][1] - r[i][1]) + r[i][0]) {
      da = !da;
    }
  }
  return da;
}

/** AN OBLIQUE WALL, LENGTHENED ALONG ITS AXIS.
 *
 *  Its box is near-square, so the box cannot say which way it runs; the
 *  written axis can. Until OB-18 such a wall was never lengthened, and with
 *  the 3 mm gaps a transcription leaves, the rooms on both sides ran
 *  together. A curved wall (`bulge`), a missing or degenerate axis (test
 *  fixtures write zero-length ones) keep the old answer: unchanged. There is
 *  no arcade test here (`daInGol` is axis-aligned), so the flag cuts every
 *  end it is set on. */
function lungitPeAxa(ring: Inel, toate: readonly Inel[], liber: boolean,
                     axis: readonly (readonly number[])[] | null | undefined,
                     bulge: number): Inel {
  if (!axis || axis.length < 2 || bulge) return ring;
  const L = Math.hypot(axis[1][0] - axis[0][0], axis[1][1] - axis[0][1]);
  if (!(L > 0.05)) return ring;
  const u: Pt = [(axis[1][0] - axis[0][0]) / L, (axis[1][1] - axis[0][1]) / L];
  const t = ring.map((p) => p[0] * u[0] + p[1] * u[1]);
  const t0 = Math.min(...t), t1 = Math.max(...t);
  const mij = (t0 + t1) / 2;
  const lungesc = (spreSus: boolean) => !liber && areVecin(ring, toate,
    ring.filter((_, i) => Math.abs(t[i] - (spreSus ? t1 : t0)) < 0.01),
    spreSus ? u : [-u[0], -u[1]]);
  const jos = lungesc(false);
  const sus = lungesc(true);
  return ring.map((p, i) => {
    const d = t[i] < mij ? (jos ? -PRELUNGIRE : 0) : (sus ? PRELUNGIRE : 0);
    return [p[0] + u[0] * d, p[1] + u[1] * d] as [number, number];
  });
}

/** BANDA UNUI ZID: linia lui de mijloc și jumătatea grosimii DESENATE.
 *
 *  Grosimea se măsoară pe poligon, nu se ia din `thickness_m`: astuparea de mai
 *  jos trebuie să cadă EXACT pe fețele desenate, iar cifra scrisă poate fi cu o
 *  zecime de milimetru lângă ele. Direcția vine din `axis` când e scrisă (o au
 *  documentele noi) și din latura cea mai lungă a dreptunghiului când nu e.
 */
function banda(w: { poly: VarfAfin[]; axis?: [[number, number], [number, number]] }):
    { c: Pt; u: Pt; t: number } | null {
  const r = inelul(w.poly).filter((q, i, a) =>
    i === 0 || q[0] !== a[0][0] || q[1] !== a[0][1]);
  if (r.length < 3) return null;
  let u: Pt | null = null;
  if (w.axis) {
    const [a, b] = w.axis;
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (l > 1e-9) u = [(b[0] - a[0]) / l, (b[1] - a[1]) / l];
  }
  if (!u) {
    let cea = 0;
    for (let i = 0; i < r.length; i++) {
      const j = (i + 1) % r.length;
      const l = Math.hypot(r[j][0] - r[i][0], r[j][1] - r[i][1]);
      if (l > cea) { cea = l; u = [(r[j][0] - r[i][0]) / l, (r[j][1] - r[i][1]) / l]; }
    }
  }
  if (!u || !Number.isFinite(u[0]) || !Number.isFinite(u[1])) return null;
  const c: Pt = [r.reduce((s, q) => s + q[0], 0) / r.length,
                 r.reduce((s, q) => s + q[1], 0) / r.length];
  const n: Pt = [u[1], -u[0]];
  const t = Math.max(...r.map(
    (q) => Math.abs((q[0] - c[0]) * n[0] + (q[1] - c[1]) * n[1])));
  return t > 1e-9 ? { c, u, t } : null;
}

/** GOLURILE, ASTUPATE LA LOC — ÎN BANDA ZIDULUI LOR, NICIODATĂ ÎN AFARA EI.
 *
 *  Zidurile catalogului sunt TĂIATE la fiecare ușă și fereastră: așa au fost
 *  măsurate golurile — planșa taie stratul ZIDURI chiar în dreptul lor, iar
 *  spațiul dintre două bucăți coliniare dă deodată poziția, lățimea și direcția
 *  (`origin: "cut"`). Deci fiecare gol e o spărtură adevărată în poligon, prin
 *  care spațiul liber curge dintr-o cameră în alta. Măsurat: fără astuparea
 *  lor, tot etajul lui M2 ieșea o singură bucată de 81 m².
 *
 *  ASTUPAREA SE AȘAZĂ PE LINIA ZIDULUI, nu pe ancora golului, și are EXACT
 *  grosimea lui. Era un dreptunghi centrat pe gol, cu 8 cm peste grosime de
 *  fiecare parte — adică un dop care iese 8 cm în cameră pe toată lățimea
 *  ferestrei. Pe lângă fiecare fereastră și fiecare ușă rămânea o fâșie albă
 *  care nu era nici zid, nici cameră; pe TEST-COTE livingul pierdea 0,33 m²,
 *  iar apartamentul întreg vreo 5. E chiar greșeala pe care `PRELUNGIRE` o
 *  numește mai sus: un zid LUNGIT își închide îmbinările, unul ÎNGROȘAT mănâncă
 *  din camere.
 *
 *  Marja rămâne, dar numai PE LUNGIME: acolo nu poate strica nimic, fiindcă
 *  intră în zidul de alături.
 *
 *  ȘI PE ZIDURILE NETĂIATE NU MAI FACE NIMIC, ceea ce e tot ce trebuie să facă.
 *  Planul citit dintr-o imagine (`importer/plan/emite.py`) scrie zidul ÎNTREG,
 *  cu golul declarat pe el — n-are nicio spărtură de astupat. Dopul de dinainte
 *  mușca din cameră pe fiecare gol, degeaba; ăsta cade înăuntrul zidului, care
 *  oricum se scade, deci nu schimbă nimic.
 */
function astupate(
  plan: NonNullable<TronsonDoc["plan"]>,
  pereti: NonNullable<NonNullable<TronsonDoc["plan"]>["walls"]>,
): Inel[] {
  const benzi = new Map(pereti.map((w) => [w.id, banda(w)]));
  const gros = new Map(pereti.map((w) => [w.id, w.thickness_m]));
  return [...(plan.doors ?? []), ...(plan.windows ?? [])].map((g) => {
    const w = (g.w_m ?? 0.9) / 2 + 0.05;
    const b = g.wall ? benzi.get(g.wall) ?? null : null;
    if (b) {
      // Golul, proiectat pe linia zidului: ancora lui poate sta cu câțiva
      // milimetri lângă ea, iar un dop mutat atâta ar lăsa o fantă pe o față.
      const d = (g.x.a - b.c[0]) * b.u[0] + (g.y.a - b.c[1]) * b.u[1];
      const px = b.c[0] + b.u[0] * d;
      const py = b.c[1] + b.u[1] * d;
      const [jos, sus] = foileGolului(pereti, benzi, b, px, py, w);
      const n0 = b.u[1], n1 = -b.u[0];
      return [
        [px - b.u[0] * w + n0 * sus, py - b.u[1] * w + n1 * sus],
        [px + b.u[0] * w + n0 * sus, py + b.u[1] * w + n1 * sus],
        [px + b.u[0] * w + n0 * jos, py + b.u[1] * w + n1 * jos],
        [px - b.u[0] * w + n0 * jos, py - b.u[1] * w + n1 * jos],
      ] as Inel;
    }
    // FĂRĂ ZIDUL LUI nu se știe nici linia, nici grosimea: rămâne dopul de
    // dinainte, generos pe amândouă direcțiile. Două camere unite ar fi mai rău
    // decât o fâșie mâncată, iar în documentele de azi nu există asemenea gol.
    const t = ((g.wall ? gros.get(g.wall) : undefined) ?? 0.3) / 2 + 0.08;
    const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
    const ux = Math.cos(rad);
    const uy = Math.sin(rad);
    const cx = g.x.a;
    const cy = g.y.a;
    return [
      [cx - ux * w + uy * t, cy - uy * w - ux * t],
      [cx + ux * w + uy * t, cy + uy * w - ux * t],
      [cx + ux * w - uy * t, cy + uy * w + ux * t],
      [cx - ux * w - uy * t, cy - uy * w + ux * t],
    ] as Inel;
  });
}

/** HOW FAR ACROSS ITS WALL AN OPENING'S PLUG GOES: the host leaf, then every
 *  parallel leaf lying face to face with it beside the opening, until none is
 *  left. Returned as `[jos, sus]` along the host band's normal, from the
 *  opening's point on the host's centre line.
 *
 *  A leaf counts only when it TOUCHES the range from outside (within 1 cm),
 *  never when it overlaps it: a thicker continuation of the same wall line
 *  overlaps the host's faces, and plugging over its thickness would cut a
 *  slice of the room in front of the door.
 *
 *  And only when it stands on BOTH sides of the opening (cut at it, as the
 *  host is, or running past it whole): a parallel wall that merely ends beside
 *  the door is another room's wall, and plugging across it took the floor in
 *  front of three M5 doors (0.13–0.28 m² each). */
function foileGolului(
  pereti: NonNullable<NonNullable<TronsonDoc["plan"]>["walls"]>,
  benzi: Map<string, ReturnType<typeof banda>>,
  b: { c: Pt; u: Pt; t: number }, px: number, py: number, w: number,
): [number, number] {
  const n: Pt = [b.u[1], -b.u[0]];
  // The gap's half width: the plug is the gap plus 5 cm on each side.
  const jum = w - 0.05;
  const felii = pereti.flatMap((z) => {
    const bz = benzi.get(z.id);
    if (!bz || Math.abs(b.u[0] * bz.u[1] - b.u[1] * bz.u[0]) >= 0.01) return [];
    const r = inelul(z.poly);
    const de = r.map((q) => (q[0] - px) * b.u[0] + (q[1] - py) * b.u[1]);
    const pe = r.map((q) => (q[0] - px) * n[0] + (q[1] - py) * n[1]);
    return [{ d0: Math.min(...de), d1: Math.max(...de),
              c0: Math.min(...pe), c1: Math.max(...pe) }];
  });
  /** Is there wall on both sides of the gap between `c0` and `c1` across? */
  const peAmbeleParti = (c0: number, c1: number) => {
    const linie = felii.filter((f) => Math.abs(f.c0 - c0) <= 0.01 && Math.abs(f.c1 - c1) <= 0.01);
    const stanga = linie.some((f) => f.d0 <= -jum + 0.02 && f.d1 >= -w);
    const dreapta = linie.some((f) => f.d1 >= jum - 0.02 && f.d0 <= w);
    return stanga && dreapta;
  };
  let jos = -b.t, sus = b.t;
  for (let crescut = true; crescut;) {
    crescut = false;
    for (const f of felii) {
      if (f.d1 < -w || f.d0 > w) continue;
      if (f.c1 > sus + 1e-6 && Math.abs(f.c0 - sus) <= 0.01 && peAmbeleParti(f.c0, f.c1)) {
        sus = f.c1; crescut = true;
      } else if (f.c0 < jos - 1e-6 && Math.abs(f.c1 - jos) <= 0.01
                 && peAmbeleParti(f.c0, f.c1)) {
        jos = f.c0; crescut = true;
      }
    }
  }
  return [jos, sus];
}

/** Un inel închis, cum îl vrea `polygon-clipping`. */
const inchis = (r: Inel): Inel =>
  (r.length && (r[0][0] !== r[r.length - 1][0] || r[0][1] !== r[r.length - 1][1])
    ? [...r, r[0]] : r);

function arie(r: Inel): number {
  let s = 0;
  for (let i = 0; i < r.length - 1; i++) {
    s += r[i][0] * r[i + 1][1] - r[i + 1][0] * r[i][1];
  }
  return Math.abs(s) / 2;
}

/** Aria comună a două inele — pe ea se face potrivirea. */
function suprapunere(a: Inel, b: Inel): number {
  try {
    return taie().intersection([inchis(a)], [inchis(b)])
      .reduce((s, poly) => s + arie(inchis(poly[0] as Inel)), 0);
  } catch {
    return 0;                       // geometrie degenerată: nu se potrivește
  }
}

/** `k`-ul celui mai apropiat vârf vechi. Vezi nota de sus: fără el, modulul
 *  își pierde elasticitatea la prima mutare de perete. */
function kApropiat(p: [number, number], vechi: VarfAfin[] | null): [number, number] {
  if (!vechi?.length) return [0, 0];
  let best = vechi[0];
  let bd = Infinity;
  for (const q of vechi) {
    const d = (q[0].a - p[0]) ** 2 + (q[1].a - p[1]) ** 2;
    if (d < bd) { bd = d; best = q; }
  }
  return [best[0].k ?? 0, best[1].k ?? 0];
}

/** Dreptunghiul subțire cu care taie un prag. */
function bandaPragului(seg: VarfAfin[]): Inel | null {
  const [a, b] = inelul(seg);
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l = Math.hypot(dx, dy);
  if (l < 1e-9) return null;
  // Prelungit la capete cu propria lățime: pragul se sprijină pe câte un zid,
  // iar dacă se oprește exact pe fața lui rămâne o punte de câțiva microni prin
  // care derivarea curge mai departe și camerele se unesc oricum.
  const ex = (dx / l) * PRAG_LAT;
  const ey = (dy / l) * PRAG_LAT;
  const nx = (-dy / l) * (PRAG_LAT / 2);
  const ny = (dx / l) * (PRAG_LAT / 2);
  const p0 = [a[0] - ex, a[1] - ey];
  const p1 = [b[0] + ex, b[1] + ey];
  return inchis([
    [p0[0] + nx, p0[1] + ny], [p1[0] + nx, p1[1] + ny],
    [p1[0] - nx, p1[1] - ny], [p0[0] - nx, p0[1] - ny],
  ]);
}

/** Bucățile de spațiu liber rămase între ziduri și praguri.
 *
 *  Editorul le cere o dată, la deschidere, și le ține ca reper: o cameră a
 *  cărei bucată arată la fel ca atunci rămâne scrisă exact cum a fost scrisă. */
/** `[u0, v0, u1, v1]` ca inel închis — forma pe care o cere tăietorul. */
function dreptunghi([u0, v0, u1, v1]: number[]): Inel {
  return inchis([[u0, v0], [u1, v0], [u1, v1], [u0, v1]]);
}


/** SPAȚIUL ÎN CARE S-AU CĂUTAT ULTIMA DATĂ camerele — marginea lui e marginea
 *  DESENULUI. Ținut deoparte, nu întors, fiindcă îl cere un singur chemător
 *  (`deschisaSpreAfara`) iar semnătura lui `bucatileDerivate` e citită în zece
 *  locuri. Se rescrie la fiecare derivare, deci se citește imediat după. */
let ultimulSpatiu: pc.MultiPolygon = [];

/** BUCATA IESE ÎN AFARA DESENULUI? — adică nu e închisă de ziduri.
 *
 *  O cameră e un gol ÎNCHIS. Spațiul liber se oprește ori pe fața unui zid —
 *  și atunci stă strict înăuntrul spațiului căutat —, ori pe marginea
 *  desenului, și atunci nu-l închide nimic: e colțul rămas deschis după ce o
 *  fațadă a fost trasă în afară. Pe ăla nu se naște o cameră (01.10.2026: «if
 *  there are no walls around the flooring it should not drag it along»).
 *
 *  NU SE MĂSOARĂ CÂT DIN MARGINE ARE ZID. Am încercat: pe catalog, camerele
 *  adevărate ies între 0,53 și 1,00, iar bucata deschisă din filmare la 0,55 —
 *  cele două nu se despart, deci orice prag ar fi tăiat prin camere bune.
 *  Atingerea marginii e însă o însușire, nu o măsură.
 */
export function deschisaSpreAfara(inel: Inel): boolean {
  const E = 1e-6;
  for (const poly of ultimulSpatiu) {
    for (const ring of poly) {
      for (let i = 0; i < ring.length - 1; i++) {
        const a = ring[i] as Pt;
        const b = ring[i + 1] as Pt;
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const L2 = dx * dx + dy * dy;
        if (L2 < E) continue;
        for (const q of inel) {
          const t = Math.max(0, Math.min(1, ((q[0] - a[0]) * dx + (q[1] - a[1]) * dy) / L2));
          const px = a[0] + dx * t, py = a[1] + dy * t;
          if (Math.hypot(q[0] - px, q[1] - py) < 1e-3) return true;
        }
      }
    }
  }
  return false;
}

export function bucatileDerivate(doc: TronsonDoc, fereastra?: Cutie | null): Inel[] | null {
  const plan = doc.plan;
  /** Zidurile cărora omul le-a lăsat capetele libere — v. `lungit`. */
  const libere = new Set(plan?.capete_libere ?? []);
  const pereti0 = plan?.walls ?? [];
  if (!plan || !pereti0.length) return null;
  // FEREASTRA: derivarea locală (v. `cuCamereDerivate`) n-are nevoie de tot
  // nivelul, ci de cutia din jurul zonei atinse. Pe un etaj întreg de soluție
  // (340 de pereți, 93 de camere) derivarea completă costa 214 ms — pe fiecare
  // mișcare a mouse-ului cât ține o tragere.
  const inFereastra = (r: Inel) => {
    if (!fereastra) return true;
    const c = cutia(r);
    return c[0] <= fereastra[2] && c[2] >= fereastra[0]
        && c[1] <= fereastra[3] && c[3] >= fereastra[1];
  };
  const pereti = fereastra ? pereti0.filter((w) => inFereastra(inelul(w.poly))) : pereti0;
  if (!pereti.length) return null;
  // Every wall of the plan as a neighbour, also in a windowed derivation:
  // whether an end has something to close must not depend on the window. The
  // openings' plugs too: a jamb a few cm short of its door's plug (a door set
  // off-centre in its opening) has that slot to close.
  const inelePereti = [...pereti0.map((x) => inelul(x.poly)), ...astupate(plan, pereti0)];
  const lungitInPlan = (w: (typeof pereti0)[number]) => lungit(
    inelul(w.poly), inelePereti, libere.has(w.id),
    w.axis as [[number, number], [number, number]] | undefined,
    (w as { bulge?: number }).bulge ?? 0);

  // ANVELOPA SE STRÂNGE PÂNĂ LA FEȚELE EXTERIOARE ALE ZIDURILOR.
  //
  // Cea declarată se compune din `front.nominal_m` și adâncimea benzii, și e mai
  // mare decât clădirea: pe M2, fațada stă la y = ±7,504 într-o anvelopă care
  // merge la ±7,65. Fâșia de 14 cm dintre ele înconjoară tot modulul și leagă
  // fiecare cameră de fiecare — scăzând zidurile, ieșea O SINGURĂ bucată de 82
  // m² în loc de nouă camere. Deci se ia intersecția cu cadrul zidurilor, unde
  // marginea chiar e fața zidului de fațadă.
  const toate = pereti0.flatMap((w) => inelul(w.poly));
  const xs = toate.map((p) => p[0]);
  const ys = toate.map((p) => p[1]);
  const cadru: Inel = [
    [Math.min(...xs), Math.min(...ys)], [Math.max(...xs), Math.min(...ys)],
    [Math.max(...xs), Math.max(...ys)], [Math.min(...xs), Math.max(...ys)],
  ];

  // …ȘI SE LĂRGEȘTE PÂNĂ UNDE AJUNG ZIDURILE.
  //
  // Anvelopa DECLARATĂ nu se mișcă la tragerea unui singur zid, și bine face:
  // `model::conturUrmeazaZidurile` mută o latură după MEDIANA zidurilor ei,
  // tocmai ca o cameră ieșită în bovindou să nu mute frontul întregii bare —
  // frontul e pasul barei pentru planul de situație.
  //
  // Dar camerele se derivă TĂIATE la ea, așa că o fațadă trasă în afară creștea
  // camera doar cât mai era loc până la marginea declarată, și de-acolo încolo
  // nu mai creștea deloc: pe M2, trasă cu 0,30 m, camera urca de la 15,06 la
  // 15,94; trasă cu 0,60, ajungea la 16,20 în loc de 16,8. Pe planurile unde
  // zidurile ies de la bun început din anvelopă (M6 declară 13,50 peste ziduri
  // care se întind pe 13,80), fâșia dinafară era pierdută cu totul.
  //
  // Deci spațiul în care se caută camerele e anvelopa REUNITĂ cu ce închid
  // zidurile: conturul lor exterior, umplut. Numai inelul de AFARĂ al fiecărei
  // bucăți de zidărie — găurile ei sunt chiar camerele, iar umplute se scad la
  // loc mai jos, odată cu zidurile. Reuniunea cu anvelopa, nu înlocuirea ei:
  // un balcon în consolă stă în afara zidurilor și trebuie să rămână afară.
  const umplut: pc.Polygon[] = [];
  try {
    const zidite = taie().union(
      pereti0.map((w) => [inchis(lungitInPlan(w))] as pc.Polygon),
      ...astupate(plan, pereti0).map((r) => [inchis(r)] as pc.Polygon));
    // Doar inelul exterior: `poly[0]`. Restul sunt găurile — camerele.
    for (const poly of zidite) umplut.push([poly[0]] as pc.Polygon);
  } catch { /* geometrie degenerată: rămâne anvelopa declarată */ }

  let bucati: pc.MultiPolygon;
  let spatiu: pc.MultiPolygon = [];
  try {
    const env = taie().intersection(
      taie().union([inchis(anvelopa(doc) as Inel)] as pc.Polygon, ...umplut),
      [inchis(cadru)],
      ...(fereastra ? [[dreptunghi(fereastra)] as pc.Polygon] : []));
    if (!env.length) return null;
    spatiu = env;
    bucati = taie().difference(
      env,
      ...pereti.map((w) => [inchis(lungitInPlan(w))] as pc.Polygon),
      ...astupate(plan, pereti0).filter(inFereastra).map((r) => [inchis(r)] as pc.Polygon),
      // NUCLEUL NU E PODEA. Puțul scării și cele de lift urcă de la radier la
      // acoperiș: nu sunt cameră, nu sunt coridor, nu se vând. Derivarea nu-l
      // știa deloc, deci bucata holului îl înghițea — iar cum ea rescrie doar
      // ce s-a schimbat de la deschidere, greșeala apărea abia la prima
      // atingere a modulului.
      //
      // Măsurat pe M5 (17.09.2026): beneficiarul a șters o ușă și a publicat,
      // iar holul a ieșit peste 15,38 m² de nucleu — puț socotit coridor, care
      // intră în `useful_m2` și de acolo în fiecare bară compusă din modul.
      // Exact reparația pe care `tools/declara_puturile_de_lift.py` o făcuse cu
      // mâna în septembrie, desfăcută de o salvare din editor.
      ...(plan.core_rect && plan.core_rect.length === 4
          ? [[dreptunghi(plan.core_rect)] as pc.Polygon] : []),
      // Every part of the core too, grown to its shaft walls: on M3 the lifts
      // and the shaft lie outside `core_rect`, and the hall took them (OB-18).
      ...(plan.core_parts ?? []).filter((p) => (p.poly?.length ?? 0) >= 3)
        .flatMap((p) => crescutaLaZiduri(cutia(p.poly as Inel), pereti0)
          .map((c) => [dreptunghi(c)] as pc.Polygon)),
      ...(plan.thresholds ?? [])
        .map(bandaPragului)
        .filter((r): r is Inel => r != null)
        .map((r) => [r] as pc.Polygon),
    );
  } catch {
    return null;
  }
  if (!bucati.length) return null;

  // A wall standing free inside a piece is a hole in it; kept as a keyhole
  // ring, it stays out of the floor and out of the area (OB-18).
  const bucatile = bucati.map((poly) => (poly.length > 1
    ? inchis(inelCuGaura(poly as unknown as Pt[][]) as Inel)
    : inchis(poly[0] as Inel)));
  // FÂȘIILE SE COS DE CAMERA DE LÂNGĂ ELE, nu se aruncă — v. `cuFasiile`.
  // Raza se ia din documentul ăsta: fâșia trebuie să vadă peste cel mai gros
  // zid din plan, iar grosimile merg de la 10 cm la peste un metru.
  const grosMax = pereti.reduce(
    (m, w) => Math.max(m, (w as { thickness_m?: number }).thickness_m ?? 0.3), 0.3);
  // Set before stitching: `esteFasie` asks `deschisaSpreAfara`, which reads it.
  ultimulSpatiu = spatiu;
  const iesite = cuFasiile(
    bucatile.filter((r) => arie(r) >= ASCHIE_M2),
    bucatile.filter((r) => arie(r) < ASCHIE_M2 && arie(r) >= ZGOMOT_M2),
    grosMax + 0.1,
    (plan.thresholds ?? []).map(inelul));
  return iesite;
}

/** Sub atâta, o bucată nu e nici fâșie, e zgomotul tăierii booleene. */
const ZGOMOT_M2 = 0.01;

/** Distanța de la un punct la conturul unui inel. */
function panaLaInel(p: [number, number], r: Inel): number {
  let cel = Infinity;
  for (let i = 0; i < r.length; i++) {
    const a = r[i], b = r[(i + 1) % r.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const L = dx * dx + dy * dy;
    const t = L ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L)) : 0;
    const qx = a[0] + t * dx, qy = a[1] + t * dy;
    cel = Math.min(cel, Math.hypot(p[0] - qx, p[1] - qy));
  }
  return cel;
}

/** CÂT DE MULT SE UITĂ FÂȘIA LA O CAMERĂ: lungimea conturului ei care trece pe
 *  lângă camera aia, la cel mult `raza` — adică exact «latura comună», măsurată
 *  PESTE zidul dintre ele, fiindcă fâșia și camera nu se ating niciodată. */
function catSeInvecineaza(mica: Inel, mare: Inel, raza: number): number {
  let L = 0;
  for (let i = 0; i < mica.length; i++) {
    const a = mica[i], b = mica[(i + 1) % mica.length];
    const mij: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    if (panaLaInel(mij, mare) <= raza) L += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return L;
}

/** FÂȘIA SE COASE DE CAMERĂ CU UN CORIDOR DE LĂȚIME ZERO.
 *
 *  Aceeași «gaură de cheie» ca `model::inelCuGaura`, cu o deosebire care
 *  schimbă totul: acolo gaura se parcurge în sens INVERS, ca aria ei să se
 *  SCADĂ; aici fâșia se parcurge în ACELAȘI sens, ca aria ei să se ADUNE. Un
 *  coridor de lățime zero nu ia nicio arie, deci aria camerei crește exact cu
 *  fâșia — nu și cu zidul peste care trece cusătura. Asta contează: aria
 *  tipărită intră în `useful_m2`, iar un zid socotit cameră ar umfla suprafața
 *  vândută (v. incidentul puțului de lift din nota de mai sus). */
function cusutaLa(mare: Inel, mica: Inel): Inel {
  const fara = (r: Inel): Inel => {
    const q: Inel = r.slice();
    if (q.length > 1 && q[0][0] === q[q.length - 1][0]
        && q[0][1] === q[q.length - 1][1]) q.pop();
    return q;
  };
  const semnat = (r: Inel) => r.reduce((t, q, i) => {
    const n = r[(i + 1) % r.length];
    return t + q[0] * n[1] - n[0] * q[1];
  }, 0) / 2;
  const out = fara(mare);
  let h = fara(mica);
  if (out.length < 3 || h.length < 3) return mare;
  if (Math.sign(semnat(h)) !== Math.sign(semnat(out))) h = h.slice().reverse();
  let bi = 0, bj = 0, cel = Infinity;
  for (let i = 0; i < out.length; i++) {
    for (let j = 0; j < h.length; j++) {
      const d = (out[i][0] - h[j][0]) ** 2 + (out[i][1] - h[j][1]) ** 2;
      if (d < cel) { cel = d; bi = i; bj = j; }
    }
  }
  // ÎNCHIS LA LOC. Tot ce iese din `bucatileDerivate` e un inel închis (ultimul
  // vârf = primul), iar cine îl citește taie ultimul vârf ca să-l deschidă
  // (v. `refacute`). Întors deschis, cusătura rămânea fără un vârf adevărat și
  // aria ieșea pe jumătate.
  return inchis([...out.slice(0, bi + 1), ...h.slice(bj), ...h.slice(0, bj + 1),
                 ...out.slice(bi)]);
}

/** Narrower than this, a piece under `ASCHIE_M2` is a joint gap between two
 *  walls (the 26.10 strip was 0.10–0.15 wide); at 0.20 the same fixture is
 *  already a room of 0.72 m². A shaft or a passage strip is wider or is cut
 *  off by a threshold, and is not floor (OB-18). */
const FASIE_LAT = 0.20;

/** IS THIS PIECE A JOINT GAP, which belongs to the room beside it?
 *
 *  Thin: its width, the smallest extent across any of its own edge
 *  directions, is under `FASIE_LAT`. Not cut off by a threshold: a threshold
 *  says "the room changes here", so a strip behind one is nobody's (Legea 114
 *  anexa 1 excludes the floor of passages up to 1.00 m). Closed: a piece
 *  touching the edge of the drawing is a gap open to the outside, such as a
 *  facade leaf cut at a window. */
function esteFasie(f: Inel, praguri: readonly Inel[]): boolean {
  let lat = Infinity;
  for (let i = 0; i < f.length - 1; i++) {
    const dx = f[i + 1][0] - f[i][0], dy = f[i + 1][1] - f[i][1];
    const L = Math.hypot(dx, dy);
    if (L < 1e-6) continue;
    const nx = -dy / L, ny = dx / L;
    const pe = f.map((p) => p[0] * nx + p[1] * ny);
    lat = Math.min(lat, Math.max(...pe) - Math.min(...pe));
  }
  if (!(lat < FASIE_LAT)) return false;
  // Both ways: a transcribed threshold stops 1 cm short of its walls, so a
  // strip whose corners sit on the wall faces is 11.2 mm from the threshold's
  // ends, while the threshold's own points lie on the strip's edge.
  if (praguri.some((pr) => pr.length >= 2
      && (f.some((p) => panaLaInel(p, pr) <= PRAG_LAT + 0.001)
          || pr.some((q) => panaLaInel(q, f) <= PRAG_LAT + 0.001)))) return false;
  return !deschisaSpreAfara(f);
}

/** FÂȘIILE NU SE PIERD, INTRĂ ÎN CAMERA DE LÂNGĂ ELE.
 *
 *  Cerut de arhitect (26.10.2026): «câteodată nu pune pardoseala într-o
 *  bucățică din aia mică, albă (...) să se umple tot cu pardoseală, să nu
 *  rămână nicio bucățică din asta». Aruncate, ele rămâneau GĂURI ALBE: podeaua
 *  se desenează din camere, deci o bucată care nu e cameră n-are pardoseală, și
 *  nici de umplut cu mâna nu se putea.
 *
 *  SE ALEGE CAMERA CU CARE FÂȘIA SE ÎNVECINEAZĂ CEL MAI MULT, nu cea mai
 *  apropiată: o fâșie dintr-un colț atinge două camere cu câte un vârf, iar
 *  „cea mai apropiată" ar fi ales-o pe oricare. RAZA SE MĂSOARĂ DIN DOCUMENT —
 *  cel mai gros zid plus o palmă —, fiindcă peste zidul ăla trebuie să vadă.
 *
 *  FĂRĂ NICIO CAMERĂ MARE nu se lipește nimic: rămâne ce era, adică nimic. O
 *  fâșie singură n-ar avea de cine să se prindă, iar promovată la cameră ar
 *  aduce înapoi exact ce `ASCHIE_M2` ține afară. */
function cuFasiile(mari: Inel[], fasii: Inel[], raza: number,
                   praguri: readonly Inel[] = []): Inel[] {
  if (!mari.length || !fasii.length) return mari;
  const out = mari.slice();
  for (const f of fasii) {
    if (!esteFasie(f, praguri)) continue;
    let k = -1, cel = 0;
    for (let i = 0; i < out.length; i++) {
      const L = catSeInvecineaza(f, out[i], raza);
      if (L > cel) { cel = L; k = i; }
    }
    if (k >= 0) out[k] = cusutaLa(out[k], f);
  }
  return out;
}

/** CELE DOUĂ POLIGOANE CAD ÎN ACEEAȘI BUCATĂ? — adică, după geometria de acum,
 *  sunt o singură cameră.
 *
 *  Întrebarea unirii (v. `model::uneste`): ea desface ce crede că desparte două
 *  camere și are nevoie de un răspuns care nu ghicește — dacă după desfacere
 *  camerele tot ies două, n-a desfăcut ce trebuia, iar documentul se întoarce
 *  neatins în loc să rămână cu un zid lipsă și nimic de arătat.
 *
 *  Jumătatea ariei ca prag: bucata derivată e ceva mai mică decât poligonul
 *  scris (zidurile se lungesc înainte de scădere), iar o cameră unită acoperă
 *  oricum aproape tot ce acopereau amândouă. */
export function inAceeasiBucata(doc: TronsonDoc, a: Inel, b: Inel): boolean {
  const bucati = bucatileDerivate(doc);
  if (!bucati) return false;
  const aa = arie(inchis(a));
  const ab = arie(inchis(b));
  return bucati.some((r) => suprapunere(r, a) > aa / 2
                         && suprapunere(r, b) > ab / 2);
}

/** Bucata asta arată exact ca la deschidere?
 *
 *  Derivarea și transcrierea nu dau aceleași numere: prelungirea zidurilor
 *  mușcă din cameră, așa că livingul lui M2 iese derivat cu vreun procent mai
 *  mic decât scrie în document. Dacă orice mutare de zid ar rescrie TOATE
 *  camerele, diferența asta s-ar scurge dintr-o dată în ariile declarate ale
 *  apartamentelor — iar acelea sunt măsurate, nu calculate din desen. Deci se
 *  rescrie doar ce s-a schimbat de la deschidere încoace.
 */
function neatinsa(r: Inel, baza: Inel[] | null | undefined): boolean {
  if (!baza?.length) return false;
  const a = arie(r);
  return baza.some((b) => Math.abs(arie(b) - a) < 1e-3
                       && suprapunere(r, b) > a - 1e-3);
}

/** Bucata de la deschidere pe care o arată `r`, sau `null`. */
function laBaza(r: Inel, baza: ReperCamere | null | undefined)
  : BucataReper | null {
  if (!baza?.length) return null;
  const a = arie(r);
  return baza.find((b) => Math.abs(arie(b.inel) - a) < 1e-3
                       && suprapunere(r, b.inel) > a - 1e-3) ?? null;
}

/** O bucată de la deschidere, cu camera care era scrisă pe ea atunci. */
export type BucataReper = { inel: Inel; camera: CameraDoc | null };
export type ReperCamere = BucataReper[];

/** REPERUL CAMERELOR — bucățile de la deschidere ȘI CAMERA FIECĂREIA.
 *
 *  Reperul ține minte cum arăta modulul înainte ca cineva să se atingă de el,
 *  ca o cameră neclintită să rămână scrisă cuvânt cu cuvânt (v. `neatinsa`).
 *  Multă vreme a fost o simplă listă de inele, iar camera scrisă se lua din
 *  documentul de ACUM — ceea ce merge exact cât timp nimeni nu s-a întors de
 *  unde a plecat.
 *
 *  ZIDUL DUS ȘI ADUS ÎNAPOI e chiar drumul ăla. Pe M2: săgeata dreapta mută
 *  peretele cu 5 cm, livingul se rescrie 15,09 -> 15,15; săgeata stânga îl
 *  aduce la loc, bucata redevine cea de la deschidere — și tocmai fiindcă
 *  redevine, camera nu se mai rescrie, ci se păstrează cea „veche", care între
 *  timp ajunsese 15,15. Aria rămânea greșită, iar poligonul camerei rămânea
 *  mutat cu 5 cm față de zidurile lui, până la un ⌘Z sau la o reîncărcare.
 *  Numărul din panou e prima confirmare că gestul a fost înțeles, deci unul
 *  care nu se mai întoarce e mai rău decât unul care nu s-ar fi mișcat deloc.
 *
 *  Deci reperul poartă camera AȘA CUM ERA SCRISĂ ATUNCI, iar bucata întoarsă
 *  acasă își ia înapoi geometria și aria de atunci — nu și felul, latura sau
 *  apartamentul, care sunt ale omului și se pot fi schimbat între timp din
 *  panou (v. `caLaDeschidere`).
 *
 *  O BUCATĂ CU DOI LOCATARI N-ARE CAMERĂ AICI. E cazul a două încăperi care
 *  comunică fără zid între ele: bucata lor se împarte înapoi la derivare (v.
 *  `impartita`), deci nu există o cameră a ei pe care s-o ții minte. Alea
 *  rămân pe drumul dinainte — poligonul scris al camerei le e reper.
 *
 *  Potrivirea costă o suprapunere pe fiecare pereche bucată-cameră: pe M5, cel
 *  mai mare modul din catalog, 4 ms peste derivarea singură (11 față de 7),
 *  plătiți O DATĂ, la deschidere. */
export function reperulCamerelor(doc: TronsonDoc): ReperCamere | null {
  const inele = bucatileDerivate(doc);
  if (!inele) return null;
  // Spațiul exterior nu se derivă și nu intră în împărțeală (v. `impartita`).
  const camere = (doc.plan?.rooms ?? []).filter((c) => c.kind !== "balcony");
  return inele.map((inel) => {
    // Locatarii, măsurați ca în `impartita`: camerele care chiar stau în
    // bucata asta, nu cele care o ating pe-alături.
    const locatari = camere.filter((c) => {
      const p = inchis(inelul(c.poly));
      return suprapunere(inel, p) > arie(p) / 2;
    });
    return { inel, camera: locatari.length === 1 ? locatari[0] : null };
  });
}

/** Camera de acum, cu geometria și aria de la deschidere.
 *
 *  Se întoarce chiar obiectul vechi când n-ar schimba nimic: pe un modul în
 *  care nimeni n-a mutat nimic, derivarea trebuie să lase documentul identic,
 *  nu doar echivalent. */
function caLaDeschidere(veche: CameraDoc, atunci: CameraDoc | null): CameraDoc {
  if (!atunci) return veche;
  if (atunci.area_m2 === veche.area_m2
      && JSON.stringify(atunci.poly) === JSON.stringify(veche.poly)) return veche;
  return { ...veche, poly: atunci.poly, area_m2: atunci.area_m2 };
}

/** Inelul cel mai mare dintr-un rezultat boolean — sau `null`, dacă n-a rămas
 *  din el nimic cât o cameră. */
function celMaiMare(m: pc.MultiPolygon): Inel | null {
  let cel: Inel | null = null;
  let cea = ASCHIE_M2;
  for (const poly of m) {
    const r = inchis(poly[0] as Inel);
    const a = arie(r);
    if (a >= cea) { cea = a; cel = r; }
  }
  return cel;
}

/** Bucata, ca `{r}`, și camera veche a cărei e, dacă se știe deja (`fix`). */
type Bucata = { r: Inel; fix: number };

/** CAMERELE NU SE UNESC SINGURE. Bucata cu mai mulți locatari, împărțită înapoi
 *  între ei.
 *
 *  Derivarea spune unde sunt PEREȚII; ea nu poate spune că două camere au
 *  devenit una. Scos zidul dintre bucătărie și living, spațiul liber curge
 *  dintr-una într-alta și iese o singură bucată — iar potrivirea pe suprapunere
 *  o dădea celei pe care o acoperea mai mult, cealaltă dispărând din listă cu
 *  tot cu aria ei declarată și cu finisajul ei. Cerut explicit (16.09.2026):
 *  «să nu se unească camerele automat niciodată». Despărțitul rămâne automat,
 *  fiindcă acolo geometria chiar afirmă ceva nou: un zid tras prin mijloc.
 *
 *  Unirea se poate în continuare — dar se CERE, din butonul «Unește camerele»,
 *  iar acela scoate camera absorbită din document (v. `model::uneste`). Cât
 *  timp ea e scrisă, e o cameră.
 *
 *  LOCATAR e camera veche care stă în cea mai mare parte a ei în bucată —
 *  aceeași jumătate de arie ca la `inAceeasiBucata`. Fiecare își primește
 *  înapoi propriul poligon, tăiat la bucată; ce rămâne pe deasupra — fâșia
 *  zidului scos — merge la cel mai mare dintre ei, ca spațiul deschis să nu
 *  rămână cu o crăpătură nimănui pe locul peretelui.
 */
function impartita(r: Inel, vechi: readonly CameraDoc[]): Bucata[] {
  const locatari = vechi
    .map((c, i) => ({ c, i }))
    // Spațiul exterior nu intră în împărțeală: el nu se derivă deloc.
    .filter(({ c }) => c.kind !== "balcony")
    .map(({ c, i }) => {
      const inel = inchis(inelul(c.poly));
      return { i, inel, jumate: arie(inel) / 2, s: suprapunere(r, inel) };
    })
    .filter((x) => x.s > x.jumate)
    .sort((a, b) => b.s - a.s);
  if (locatari.length < 2) return [{ r, fix: -1 }];
  const out: Bucata[] = [];
  let rest: pc.MultiPolygon = [[r]] as pc.MultiPolygon;
  try {
    for (const l of locatari.slice(1)) {
      const parte = celMaiMare(taie().intersection([r] as pc.Polygon, [l.inel] as pc.Polygon));
      if (!parte) continue;
      out.push({ r: parte, fix: l.i });
      // Se scade EXACT ce s-a dat: partea vine din aceeași tăietură ca bucata,
      // deci marginile lor cad una peste alta și restul iese curat.
      rest = taie().difference(rest, [parte] as pc.Polygon);
    }
  } catch {
    return [{ r, fix: -1 }];        // geometrie degenerată: bucata rămâne una
  }
  const ramas = celMaiMare(rest);
  if (!ramas) return out.length ? out : [{ r, fix: -1 }];
  return [...out, { r: ramas, fix: locatari[0].i }];
}

/** CAMERELE, refăcute din pereți.
 *
 *  Întoarce `null` când derivarea n-are ce spune — niciun perete, sau operația
 *  booleană a picat pe o geometrie degenerată. Atunci se păstrează camerele
 *  vechi: un plan fără camere ar fi mai rău decât unul cu camere învechite.
 *
 *  `baza` e reperul de la deschidere (v. `reperulCamerelor`): bucățile care nu
 *  s-au clintit își păstrează camera scrisă atunci, neatinsă. */
export function derivaCamere(
  doc: TronsonDoc, baza?: ReperCamere | null,
): CameraDoc[] | null {
  return refacute(doc, baza)?.camere ?? null;
}

/** Camerele refăcute, ȘI DE UNDE VINE FIECARE.
 *
 *  `dinVechi[j]` e poziția veche a camerei care a ajuns pe locul `j`, sau −1
 *  pentru o bucată nouă. Cine ține ceva legat de o cameră PRIN POZIȚIE —
 *  finisajele, azi — are nevoie de tabelul ăsta: derivarea poate scoate o
 *  cameră din mijlocul listei (cineva a scos zidul dintre două), iar atunci
 *  tot ce vine după ea alunecă cu un rând. Fără remapare, parchetul
 *  dormitorului ar ajunge în baie fără ca vreo geometrie să se fi schimbat —
 *  exact felul de greșeală pe care n-o vede nimeni până la recepție. */
export function refacute(
  doc: TronsonDoc, baza?: ReperCamere | null, fereastra?: Cutie | null,
): { camere: CameraDoc[]; dinVechi: number[] } | null {
  const plan = doc.plan;
  if (!plan) return null;
  const inele0 = bucatileDerivate(doc, fereastra);
  if (!inele0?.length) return null;
  const vechi = plan.rooms ?? [];

  // SPAȚIUL EXTERIOR NU SE DERIVĂ, SE PĂSTREAZĂ.
  //
  // Un balcon în consolă stă ÎN AFARA anvelopei, iar derivarea taie tocmai
  // acolo (v. `bucatileDerivate`): n-ar fi ieșit din ea nicio bucată, camera
  // n-ar fi găsit pereche, și balconul ar fi dispărut la primul zid mutat. Iar
  // o logie, care e înăuntru, ar fi ieșit ca bucată — dar ca o cameră oarecare,
  // fără `outdoor_kind`, adică drept suprafață utilă, ceea ce e chiar cifra pe
  // care se vinde un apartament.
  //
  // Nici nu e o scutire de complezență: o cameră e golul dintre ziduri, iar un
  // balcon nu e mărginit de ziduri — e mărginit de parapeți și de aer. Deci
  // rămâne scris cum a fost desenat, și se mută odată cu parapeții lui pe
  // același drum ca orice cameră (`model::mutaLinie` prinde toate poligoanele,
  // nu doar pe cele derivate).
  const afaraIdx = vechi.map((c, i) => (c.kind === "balcony" ? i : -1))
    .filter((i) => i >= 0);
  const afara = afaraIdx.map((i) => ({ i, inel: inelul(vechi[i].poly) }));
  // Bucata care cade peste o logie e chiar logia, derivată a doua oară: se
  // lasă, ca să nu iasă două camere pe același loc.
  const inele = afara.length
    ? inele0.filter((r) => !afara.some(
        (a) => suprapunere(r, a.inel) > arie(r) / 2))
    : inele0;

  // O BUCATĂ CU DOI LOCATARI SE ÎMPARTE ÎNAPOI ÎNTRE EI (v. `impartita`): scos
  // un zid, camerele de-o parte și de alta rămân două.
  const bucati = inele.flatMap((r) => impartita(r, vechi));

  // Fiecare bucată rămasă își caută camera veche pe care o acoperă cel mai
  // mult. O cameră veche poate fi luată o singură dată: dacă un zid nou a
  // împărțit-o în două, doar bucata mai mare o moștenește, iar cealaltă e o
  // cameră nouă.
  //
  // DAR NOUĂ DOAR CA RÂND ÎN LISTĂ, NU CA FEL. Un zid tras prin mijlocul unui
  // living face două camere, și amândouă sunt tot living: nimeni nu desenează o
  // compartimentare ca să obțină o încăpere despre care nu se știe nimic. Deci
  // pe lângă camera pe care bucata o MOȘTENEȘTE — cea care îi dă rândul, aria
  // declarată și finisajul — se ține minte și PĂRINTELE, adică cea din care a
  // fost TĂIATĂ, luată sau nu.
  //
  // Părinte e doar camera în care bucata chiar încape: o bucată care o atinge
  // pe-alături — un spațiu abia acum închis cu ziduri lângă un living — n-a
  // fost tăiată din ea și n-are de unde să știe ce e. Aceea iese cameră fără
  // fel, și rămâne așa până spune cineva (v. `feluri::FEL_IMPLICIT`).
  const luate = new Set<number>(afaraIdx);
  for (const b of bucati) if (b.fix >= 0) luate.add(b.fix);
  const potrivite = bucati.map(({ r, fix }) => {
    // Bucata împărțită știe deja a cui e: locatarul ei.
    if (fix >= 0) {
      return { r, idx: fix, veche: vechi[fix], parinte: vechi[fix] };
    }
    const jumate = arie(r) / 2;
    let cel = -1;
    let cea = 0;
    let tata = -1;
    let ceaTata = jumate;
    vechi.forEach((c, i) => {
      // Nici ca părinte: o bucată de lângă o logie n-are voie să-i ia felul și
      // să devină și ea spațiu exterior.
      if (c.kind === "balcony") return;
      const s = suprapunere(r, inelul(c.poly));
      if (s > ceaTata) { ceaTata = s; tata = i; }
      if (luate.has(i)) return;
      if (s > cea) { cea = s; cel = i; }
    });
    if (cel >= 0 && cea > 0) luate.add(cel);
    return { r, idx: cea > 0 ? cel : -1,
             veche: cel >= 0 && cea > 0 ? vechi[cel] : null,
             parinte: tata >= 0 ? vechi[tata] : null };
  });

  // Spațiile exterioare se întorc în listă pe rândul lor, ca și cum ar fi fost
  // derivate neatinse: `idx` le duce înapoi la locul din document, deci
  // finisajele și ariile declarate rămân ale lor.
  for (const { i } of afara) {
    potrivite.push({ r: inchis(inelul(vechi[i].poly)), idx: i,
                     veche: vechi[i], parinte: vechi[i] });
  }

  // ORDINEA DOCUMENTULUI, nu a operației booleene. O cameră care supraviețuiește
  // își păstrează locul în listă: `sides[].flats[].rooms` se citesc pe rând
  // împreună cu `plan.rooms`, iar o listă amestecată ar muta ariile declarate de
  // la o cameră la alta fără ca vreo geometrie să se fi schimbat. Bucățile noi,
  // care n-au corespondent, se așază la coadă.
  potrivite.sort((a, b) => (a.idx < 0 ? Infinity : a.idx)
                         - (b.idx < 0 ? Infinity : b.idx));

  const camere = potrivite.map(({ r, veche, parinte }) => {
    // Spațiul exterior se întoarce EXACT cum a fost scris: refăcut din inel ar
    // fi pierdut `outdoor_kind`, adică distincția consolă/logie, care e tocmai
    // ce nu se poate citi din formă.
    //
    // ARIA LUI SE RECITEȘTE TOTUȘI DIN POLIGON. Un balcon nu se derivă, dar se
    // MUTĂ: parapeții lui sunt ziduri, iar trași duc cu ei vârfurile camerei
    // (`model::mutaLinie` prinde toate poligoanele). Aria rămânea a formei de
    // dinainte — pe M5, parapetul tras cu 30 cm lăsa logia scrisă tot cu 5,72
    // m², adică singura cameră din plan care nu-și urma peretele. Nu e o
    // recalculare a unei cifre măsurate: în tot catalogul aria scrisă a
    // spațiilor exterioare E chiar aria desenată (M5: 5,72 = 5,72; M6: 4,28 =
    // 4,28), deci pe un balcon neatins nu se schimbă nimic.
    if (veche?.kind === "balcony") {
      const acum = Math.round(arie(inchis(inelul(veche.poly))) * 100) / 100;
      return acum === veche.area_m2 ? veche : { ...veche, area_m2: acum };
    }
    // NEATINSĂ FAȚĂ DE DESCHIDERE: bucata arată ca reperul, deci camera ei se
    // întoarce cum era scrisă ATUNCI — nu cum a rămas după o mutare care s-a
    // răzgândit (v. `reperulCamerelor`).
    const atunci = veche ? laBaza(r, baza) : null;
    if (veche && atunci) return caLaDeschidere(veche, atunci.camera);
    // Sau chiar poligonul scris al camerei — cazul locatarului căruia
    // împărțeala i-a dat înapoi exact ce avea (v. `impartita`): aria lui
    // declarată n-are de ce să se clintească fiindcă a căzut un zid la vecin.
    if (veche && neatinsa(r, [inchis(inelul(veche.poly))])) return veche;
    // Cine îi spune bucății ăsteia ce e: camera pe care o moștenește, altfel
    // cea din care a fost tăiată (v. nota de mai sus).
    const sursa = veche ?? parinte;
    // `polygon-clipping` întoarce inele închise (ultimul vârf = primul);
    // documentul le vrea deschise, ca toate celelalte poligoane ale lui.
    const varfuri = r.slice(0, -1);
    const poly: VarfAfin[] = varfuri.map((p) => {
      const [kx, ky] = kApropiat(p, sursa?.poly ?? null);
      const r3 = (v: number) => Math.round(v * 1000) / 1000;
      return [{ a: r3(p[0]), ...(kx ? { k: kx } : {}) },
              { a: r3(p[1]), ...(ky ? { k: ky } : {}) }] as VarfAfin;
    });
    return {
      // IDENTITATEA ȘI NUMELE SE MOȘTENESC NUMAI DE LA LOCATAR (`veche`), nu și
      // de la camera din care s-a tăiat bucata (`parinte`): o cameră mărită e
      // tot «Dormitor 1», dar jumătatea nouă rupsă din el nu e un al doilea
      // «Dormitor 1», iar două camere cu aceeași identitate de soluție ar
      // rescrie la salvare același obiect (v. `CameraDoc.eid`).
      ...(veche?.eid ? { eid: veche.eid } : {}),
      ...(veche?.label ? { label: veche.label } : {}),
      // Felul nu se vede din formă. O bucată care n-a ieșit din nicio cameră
      // veche — un spațiu închis abia acum cu ziduri — pornește de la felul
      // despre care nu se afirmă nimic (v. `feluri::FEL_IMPLICIT`).
      kind: sursa?.kind ?? FEL_IMPLICIT,
      side: sursa?.side ?? "A",
      ...(sursa?.flat != null ? { flat: sursa.flat } : {}),
      area_m2: Math.round(arie(r) * 100) / 100,
      poly,
    } as CameraDoc;
  });
  return { camere, dinVechi: potrivite.map((p) => p.idx) };
}

/** Documentul cu camerele refăcute. Le păstrează pe cele vechi când derivarea
 *  n-are ce spune.
 *
 *  CU `inainte` — documentul de dinaintea gestului —, DERIVAREA E LOCALĂ: se
 *  refac numai camerele din ZONA ATINSĂ de gest (pereții mutați, adăugați sau
 *  scoși, conturul mutat, nucleul), iar celelalte rămân cum erau scrise.
 *
 *  A venit din nivelurile soluției arhitecturale (`plan/nivelTronson.ts`). Pe
 *  un tronson din catalog derivarea completă se potrivește cu desenul, deci o
 *  cameră neatinsă iese oricum neatinsă (v. `reperulCamerelor`). Pe un nivel
 *  importat din DWG, nu: spațiile planșei nu sunt exact golurile dintre
 *  ziduri. Măsurat pe P+4, etajul 1: o derivare completă FĂRĂ NICIUN GEST
 *  rescria 25 de camere și ștergea 7. Un zid mutat într-un apartament nu are
 *  de ce să rescrie camerele din celălalt capăt al etajului.
 *
 *  Pe tronson cele două căi dau același rezultat, iar masa le cheamă la fel pe
 *  amândouă (v. `Masa::cuCamere`), ca un editor să nu se poarte în două
 *  feluri. */
/** CAMERELE REFĂCUTE, FĂRĂ CELE NOI ȘI DESCHISE.
 *
 *  O bucată de spațiu care iese la marginea desenului nu e închisă de nimic
 *  (v. `deschisaSpreAfara`). Trasă o fațadă în afară, colțul rămas deschis
 *  ieșea o bucată ca oricare alta și se năștea din el o «cameră necunoscută»,
 *  cu pardoseală și arie — 38,92 m² care nu sunt o încăpere.
 *
 *  NUMAI CELE NOI, și asta e tot miezul. O cameră care EXISTĂ rămâne, chiar
 *  dacă i se scot zidurile: «sometimes we can have a room and delete the walls
 *  and the flooring should not disappear automatically, it should be manually
 *  deleted» (01.10.2026). Deci regula nu șterge nimic — doar nu NAȘTE o cameră
 *  din spațiu care n-are pereți în jur. Ce e deja scris se șterge cu mâna, din
 *  buton, când omul vrea.
 *
 *  Și de-aia merge și pe coridorul care trece dintr-un capăt în celălalt al
 *  modulului (M3, M5): el atinge marginea prin construcție, dar e scris în
 *  document, deci se potrivește cu bucata lui și nu trece pe-aici. */
function faraDeschise(r: { camere: CameraDoc[]; dinVechi: number[] }, doc: TronsonDoc):
    { camere: CameraDoc[]; dinVechi: number[] } {
  // A NEW piece outside the declared envelope is not a room either: on M4 the
  // parapets of the balconies drawn beyond the facade close a space the module
  // declares in `plan.outdoor`, and it came out as two rooms (OB-18).
  const env = anvelopa(doc);
  const tine = r.camere.map((c, j) =>
    r.dinVechi[j] >= 0 || (!deschisaSpreAfara(inchis(inelul(c.poly)))
                           && inInel(centrulDeArie(inelul(c.poly)), env)));
  if (tine.every(Boolean)) return r;
  return {
    camere: r.camere.filter((_, j) => tine[j]),
    dinVechi: r.dinVechi.filter((_, j) => tine[j]),
  };
}

/** The area centroid of a ring (Gauss), keyhole rings included. */
function centrulDeArie(r: Inel): Pt {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < r.length; i++) {
    const p = r[i], q = r[(i + 1) % r.length];
    const k = p[0] * q[1] - q[0] * p[1];
    a += k; cx += (p[0] + q[0]) * k; cy += (p[1] + q[1]) * k;
  }
  if (Math.abs(a) < 1e-12) return r[0] as Pt;
  return [cx / (3 * a), cy / (3 * a)];
}

/** CAMERELE DESENATE CU MÂNA TREC NEATINSE PRIN DEDUCȚIE, iar gazda lor se
 *  taie în jurul lor.
 *
 *  Camerele se refac din rețeaua de ziduri la fiecare geometrie atinsă, fiindcă
 *  aia e singura care nu minte despre ce încăperi există. O zonă trasă de om —
 *  un duș în baie, o nișă — n-are însă ziduri ale ei: DEDUSĂ, EA NU EXISTĂ, și
 *  dispărea la primul perete mișcat (măsurat: `bath dus` înainte, `bath` după).
 *  Cerut de arhitect (22.10.2026): «vreau să trasez o secțiune care nu e
 *  înconjurată de pereți, să-i setez tipul, și să-mi apară perimetrul».
 *
 *  DOUĂ LUCRURI, nu unul: zona se pune la loc ȘI se SCADE din camera dedusă
 *  peste care stă. Numai păstrată, ea ar fi ajuns sub gazda refăcută din
 *  ziduri — două camere peste aceeași podea, exact ce refuză `adaugaZona`
 *  («camerele nu se suprapun», 14.10.2026), cu aria numărată de două ori.
 *
 *  SE LUCREAZĂ PE DOCUMENTUL DE DINAINTE, nu pe cel derivat: zonele desenate
 *  se iau din `doc`, fiindcă în cel derivat tocmai au dispărut. */
function cuDesenatelePastrate(d: TronsonDoc, doc: TronsonDoc): TronsonDoc {
  const desenate = (doc.plan?.rooms ?? []).filter((r) => r.desenata);
  if (!desenate.length) return d;
  const iesire = JSON.parse(JSON.stringify(d)) as TronsonDoc;
  const plan = iesire.plan;
  if (!plan) return d;
  plan.rooms = plan.rooms ?? [];
  const inchis = (p: Pt[]): Pt[] => (p.length && (p[0][0] !== p[p.length - 1][0]
    || p[0][1] !== p[p.length - 1][1]) ? [...p, p[0]] : p);
  const arie = (p: readonly Pt[]) => Math.abs(p.reduce((t, q, i) => {
    const r = p[(i + 1) % p.length];
    return t + q[0] * r[1] - r[0] * q[1];
  }, 0)) / 2;
  for (const z of desenate) {
    const inelZ = inelul(z.poly);
    if (inelZ.length < 3) continue;
    // gazdele deduse se taie în jurul zonei
    for (const c of plan.rooms) {
      if (c.desenata) continue;
      const g = inelul(c.poly);
      if (g.length < 3) continue;
      const comun = taie().intersection([inchis(g)] as never,
                                        [inchis(inelZ)] as never);
      if (!comun.length || arie(comun[0][0] as unknown as Pt[]) < 0.01) continue;
      const ramas = taie().difference([inchis(g)] as never,
                                      [inchis(inelZ)] as never);
      // cu tot cu găuri, ca `adaugaZona` — v. `model::inelCuGaura`
      const cel = ramas.map((q) => inelCuGaura(q as unknown as Pt[][]))
        .sort((x, y) => arie(y) - arie(x))[0];
      if (!cel || arie(cel) < 0.5) continue;      // n-a mai rămas o cameră
      const fara = cel.slice(0, -1).length >= 3 ? cel.slice(0, -1) : cel;
      const mm = (v: number) => Math.round(v * 1000) / 1000;
      c.poly = fara.map((q) => [{ a: mm(q[0]) }, { a: mm(q[1]) }]) as never;
      c.area_m2 = Math.round(arie(fara) * 100) / 100;
    }
    // …iar zona însăși se pune la loc, dacă deducția a scos-o
    if (!plan.rooms.some((c) => c.desenata && c.kind === z.kind
        && Math.abs(arie(inelul(c.poly)) - arie(inelZ)) < 0.01)) {
      plan.rooms.push(JSON.parse(JSON.stringify(z)));
    }
  }
  return iesire;
}

export function cuCamereDerivate(
  doc: TronsonDoc, baza?: ReperCamere | null, inainte?: TronsonDoc | null,
): TronsonDoc {
  return cuDesenatelePastrate(_cuCamereDerivate(doc, baza, inainte), doc);
}

function _cuCamereDerivate(
  doc: TronsonDoc, baza?: ReperCamere | null, inainte?: TronsonDoc | null,
): TronsonDoc {
  const zona = inainte ? zonaAtinsa(inainte, doc) : "tot";
  if (zona !== "tot" && !zona.length) return doc;
  if (zona === "tot") {
    const r = refacute(doc, baza);
    if (!r) return doc;
    const t = faraDeschise(r, doc);
    return cuCamerele(doc, t.camere, t.dinVechi);
  }

  const vechi = doc.plan!.rooms ?? [];
  const inZona = (poly: VarfAfin[]) => {
    const c = cutia(inelul(poly));
    return zona.some((z) => c[0] <= z[2] && c[2] >= z[0] && c[1] <= z[3] && c[3] >= z[1]);
  };
  // Fereastra derivării: zona, plus camerele pe care le atinge, plus un metru.
  // Tot ce poate ieși din zonă e înăuntru; o bucată care atinge totuși marginea
  // ferestrei a fost tăiată de ea, nu de pereți, deci nu se ia (camera veche
  // rămâne, v. mai jos).
  const cutii = [...zona, ...vechi.filter((c) => inZona(c.poly)).map((c) => cutia(inelul(c.poly)))];
  const F: Cutie = [
    Math.min(...cutii.map((c) => c[0])) - 1, Math.min(...cutii.map((c) => c[1])) - 1,
    Math.max(...cutii.map((c) => c[2])) + 1, Math.max(...cutii.map((c) => c[3])) + 1,
  ];
  const r = refacute(doc, baza, F);
  if (!r) return doc;
  const taiataDeFereastra = (poly: VarfAfin[]) => {
    const c = cutia(inelul(poly));
    return c[0] <= F[0] + 1e-6 || c[1] <= F[1] + 1e-6 || c[2] >= F[2] - 1e-6 || c[3] >= F[3] - 1e-6;
  };
  const t = faraDeschise(r, doc);
  const luate = t.camere.map((c, j) => ({ c, j }))
    .filter(({ c }) => inZona(c.poly) && (c.kind === "balcony" || !taiataDeFereastra(c.poly)));
  const inelLuate = luate.map(({ c }) => inchis(inelul(c.poly)));
  const pastrate = vechi.map((c, i) => ({ c, i })).filter(({ c }) => {
    const r0 = inchis(inelul(c.poly));
    if (inZona(c.poly)) {
      // O CAMERĂ PE CARE DERIVAREA N-O POATE PUNE rămâne cum era, nu dispare:
      // derivarea taie în anvelopă, iar un spațiu desenat dincolo de ea — un
      // balcon importat ca simplă cameră — n-ar avea din ce bucată să iasă.
      // Măsurat pe P+4: un zid mutat lângă el ștergea «Balcon» cu totul.
      const a = arie(r0);
      return a > 0 && inelLuate.every((l) => suprapunere(r0, l) < a / 2);
    }
    // O cameră din afara zonei, peste care s-a întins o cameră refăcută (două
    // camere unite prin zidul scos chiar la marginea zonei), cedează locul:
    // altfel ar ieși două camere pe aceeași pardoseală.
    return !inelLuate.some((l) => suprapunere(r0, l) > ASCHIE_M2 / 10);
  });
  const toate = [
    ...pastrate.map(({ c, i }) => ({ c, din: i })),
    ...luate.map(({ c, j }) => ({ c, din: t.dinVechi[j] })),
  ];
  // Aceeași ORDINE A DOCUMENTULUI ca în `refacute`: ce supraviețuiește își
  // păstrează rândul, ce e nou vine la coadă.
  toate.sort((a, b) => (a.din < 0 ? Infinity : a.din) - (b.din < 0 ? Infinity : b.din));
  return cuCamerele(doc, toate.map((x) => x.c), toate.map((x) => x.din));
}

/** Documentul cu lista de camere dată, iar FINISAJELE MERG DUPĂ CAMERELE LOR.
 *  Ele arată camera prin POZIȚIE, iar derivarea rearanjează lista — v. nota de
 *  la `refacute`. O cameră care a dispărut își pierde finisajul, fiindcă n-are
 *  ce să-l mai poarte. */
function cuCamerele(doc: TronsonDoc, camere: CameraDoc[], dinVechi: number[]): TronsonDoc {
  const vechiLaNou = new Map<number, number>();
  dinVechi.forEach((vechi, nou) => { if (vechi >= 0) vechiLaNou.set(vechi, nou); });
  const finisaje = (doc.plan!.finishes ?? [])
    .map((f) => {
      const nou = vechiLaNou.get(f.room);
      return nou == null ? null : { ...f, room: nou };
    })
    .filter((f): f is NonNullable<typeof f> => f != null);
  const plan = { ...doc.plan!, rooms: camere };
  if (finisaje.length) plan.finishes = finisaje; else delete plan.finishes;
  return { ...doc, plan };
}

type Cutie = [number, number, number, number];
function cutia(r: Inel): Cutie {
  const xs = r.map((p) => p[0]), ys = r.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}
/** Cât se lărgește zona atinsă: o cameră care doar ATINGE un zid mutat e tot
 *  a lui — fața zidului e latura ei. Și mai mult de-atât: derivarea lungește
 *  fiecare zid cu `PRELUNGIRE` la capete și astupă golurile cu până la 8 cm
 *  peste fața lui (v. `astupate`), deci un zid mutat schimbă bucăți până la
 *  vreo 15 cm de el. Cu 5 cm, măsurat pe tot catalogul, 123 din 1 227 de
 *  gesturi ieșeau altfel decât cu derivarea completă. */
const MARGINE_ZONA = 0.15;
const largita = (c: Cutie): Cutie =>
  [c[0] - MARGINE_ZONA, c[1] - MARGINE_ZONA, c[2] + MARGINE_ZONA, c[3] + MARGINE_ZONA];

/** Unde a atins gestul geometria din care se derivă camerele — ca listă de
 *  cutii, sau «tot» când schimbarea nu se poate încadra (pragurile, un contur
 *  cu alt număr de colțuri). */
export function zonaAtinsa(a: TronsonDoc, b: TronsonDoc): Cutie[] | "tot" {
  const pa = a.plan, pb = b.plan;
  if (!pa || !pb) return "tot";
  if (JSON.stringify(pa.thresholds ?? null) !== JSON.stringify(pb.thresholds ?? null)) {
    return "tot";
  }
  const out: Cutie[] = [];
  const zidA = new Map((pa.walls ?? []).map((w) => [w.id, w]));
  const zidB = new Map((pb.walls ?? []).map((w) => [w.id, w]));
  for (const [id, w] of zidA) {
    const w2 = zidB.get(id);
    if (w2 && JSON.stringify(w2.poly) === JSON.stringify(w.poly)
        && w2.thickness_m === w.thickness_m) continue;
    out.push(largita(cutia(inelul(w.poly))));
    if (w2) out.push(largita(cutia(inelul(w2.poly))));
  }
  for (const [id, w] of zidB) if (!zidA.has(id)) out.push(largita(cutia(inelul(w.poly))));
  const oa = pa.outline, ob = pb.outline;
  if (JSON.stringify(oa ?? null) !== JSON.stringify(ob ?? null)) {
    if (!oa || !ob || oa.length !== ob.length) return "tot";
    for (let i = 0; i < oa.length; i++) {
      if (oa[i][0] === ob[i][0] && oa[i][1] === ob[i][1]) continue;
      // Un colț mutat mișcă ambele laturi care pleacă din el.
      const vecini = [oa[(i + oa.length - 1) % oa.length], oa[i], oa[(i + 1) % oa.length],
                      ob[(i + ob.length - 1) % ob.length], ob[i], ob[(i + 1) % ob.length]];
      out.push(largita(cutia(vecini.map((p) => [p[0], p[1]] as [number, number]))));
    }
  }
  const ca = pa.core_rect, cb = pb.core_rect;
  if (JSON.stringify(ca ?? null) !== JSON.stringify(cb ?? null)) {
    for (const c of [ca, cb]) if (c?.length === 4) out.push(largita([c[0], c[1], c[2], c[3]]));
    if (!ca || !cb) return "tot";
  }
  return out;
}

/** Centrul unei camere și dreptunghiul ei util, pentru mobilare și etichete. */
export function cadrulCamerei(inel: readonly Pt[]) {
  const xs = inel.map((p) => p[0]);
  const ys = inel.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
